/**
 * Modificar Estadía — ajusta fechas y tipo de estadía de una reserva.
 *
 * Deliberadamente NO cobra ni mueve de habitación:
 *   - cobrar vive en PendingPaymentModal ("Abonar / Pagar Saldo")
 *   - mover al huésped vive en ChangeRoom.js
 * Mezclar las tres cosas en un solo formulario fue lo que causó que se
 * sobreescribiera la moneda de pago de las reservas.
 *
 * La tarifa se calcula con `calculateReservationPrice`, la misma función que
 * usa el resto del sistema: lee la Matriz de Tarifas (`room.pricing[]`) y
 * decide si multiplicar por noches según el flag `isNightly` del tipo de
 * estadía. La versión anterior comparaba el tipo contra el string 'full', que
 * ninguna reserva real tiene, y por eso nunca multiplicaba por noches.
 */

import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { getApi, patchApi } from 'views/services/api';
import { hotelInputToUTC, getHotelMoment } from 'utils/timeZone';
import { calculateReservationPrice } from 'utils/pricingUtils';

const money = (value) => Number(value || 0).toFixed(2);

const SectionTitle = ({ children }) => (
  <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.8, display: 'block', mb: 1.25 }}>
    {children}
  </Typography>
);

SectionTitle.propTypes = { children: PropTypes.node };

const SummaryRow = ({ label, value, bold, color }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 0.85 }}>
    <Typography variant="body2" color="text.secondary" fontSize="0.82rem">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={bold ? 700 : 600} fontSize={bold ? '1rem' : '0.875rem'} color={color}>
      {value}
    </Typography>
  </Stack>
);

SummaryRow.propTypes = {
  label: PropTypes.string,
  value: PropTypes.node,
  bold: PropTypes.bool,
  color: PropTypes.string
};

const EditReservation = (props) => {
  const { open, handleClose, data } = props;

  const hotel = JSON.parse(localStorage.getItem('hotelData'));
  const currency = hotel?.currency || 'USD';

  const [roomData, setRoomData] = useState([]);
  const [stayTypes, setStayTypes] = useState([]);

  const originalRate = Number(data?.totalPayment || 0);
  const advance = Number(data?.advanceAmount || 0);

  const formik = useFormik({
    initialValues: {
      stayType: data?.stayType || '',
      checkInDate: data?.checkInDate ? getHotelMoment(data.checkInDate).format('YYYY-MM-DDTHH:mm') : '',
      checkOutDate: data?.checkOutDate ? getHotelMoment(data.checkOutDate).format('YYYY-MM-DDTHH:mm') : ''
    },
    validationSchema: yup.object({
      checkInDate: yup.date().required('La fecha de entrada es requerida'),
      checkOutDate: yup
        .date()
        .min(yup.ref('checkInDate'), 'La fecha de salida debe ser posterior a la entrada')
        .required('La fecha de salida es requerida')
    }),
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      await submitChanges(values, resetForm);
    }
  });

  const room = useMemo(() => roomData.find((r) => String(r.roomNo) === String(data?.roomNo)), [roomData, data?.roomNo]);

  // Tarifa recalculada con los valores que el usuario tiene en pantalla.
  const pricing = useMemo(
    () =>
      calculateReservationPrice({
        room,
        stayTypeId: formik.values.stayType,
        stayTypesList: stayTypes,
        checkInDate: formik.values.checkInDate,
        checkOutDate: formik.values.checkOutDate
      }),
    [room, stayTypes, formik.values.stayType, formik.values.checkInDate, formik.values.checkOutDate]
  );

  const newRate = room ? Number(pricing.amount || 0) : originalRate;
  const newBalance = Number((newRate - advance).toFixed(2));
  const rateDelta = Number((newRate - originalRate).toFixed(2));

  const selectedStayType = stayTypes.find((t) => String(t._id) === String(formik.values.stayType));
  const isHourly = Boolean(selectedStayType && !selectedStayType.isNightly);

  // Los tipos por horas definen la salida a partir de la entrada, así que la
  // fecha de salida deja de ser editable y la ajustamos sola.
  useEffect(() => {
    if (pricing.checkOutDate && pricing.checkOutDate !== formik.values.checkOutDate) {
      formik.setFieldValue('checkOutDate', pricing.checkOutDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricing.checkOutDate]);

  useEffect(() => {
    if (!open) return;

    const load = async () => {
      try {
        const [rooms, types] = await Promise.all([
          getApi(`api/room/viewallrooms/${hotel?.hotelId}`),
          getApi(`api/hotel/stay-types/list/${hotel?.hotelId}`)
        ]);
        setRoomData(rooms?.data || []);
        setStayTypes(types?.data || []);
      } catch (error) {
        toast.error('No se pudieron cargar las tarifas del hotel');
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submitChanges = async (values, resetForm) => {
    try {
      const payload = {
        stayType: values.stayType,
        advanceAmount: advance,
        totalAmount: newBalance,
        checkInDate: hotelInputToUTC(values.checkInDate),
        checkOutDate: hotelInputToUTC(values.checkOutDate)
      };

      const response = await patchApi(`api/reservation/edit/${data?._id}`, payload);

      if (response.status === 200) {
        toast.success(
          rateDelta === 0
            ? 'Estadía actualizada'
            : `Estadía actualizada. La tarifa cambió en ${rateDelta > 0 ? '+' : ''}${currency} ${money(rateDelta)}.`
        );
        handleClose();
        resetForm();
        localStorage.setItem('modifiedDate', true);
        window.dispatchEvent(new Event('reservationUpdated'));
      } else {
        toast.error('No se pudo actualizar la estadía');
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'Ocurrió un error al actualizar la estadía');
    }
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', pb: 1.5, pt: 2 }}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <EventRepeatIcon color="primary" fontSize="small" />
            <Typography variant="h6" fontWeight={700} fontSize="1.1rem">
              Modificar Estadía
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 3.5 }}>
            Habitación {data?.roomNo ?? '—'} · fechas y tipo de estadía
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" sx={{ color: 'error.main' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2.5, pb: 2 }}>
        <form onSubmit={formik.handleSubmit}>
          <SectionTitle>Estadía</SectionTitle>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <FormLabel sx={{ mb: 0.5, fontSize: '0.85rem', fontWeight: 500 }}>Tipo de Estadía</FormLabel>
                <Select
                  id="stayType"
                  name="stayType"
                  value={formik.values.stayType}
                  onChange={formik.handleChange}
                  displayEmpty
                  sx={{ fontSize: '0.875rem' }}
                >
                  {stayTypes.map((type) => (
                    <MenuItem key={type._id} value={type._id}>
                      {type.label} {type.isNightly ? '(por noche)' : `(${type.hours}h)`}
                    </MenuItem>
                  ))}
                  {/* Reservas viejas guardaron 'night'/'full' o un tipo ya archivado:
                      lo mostramos para no dejar el Select fuera de rango. */}
                  {formik.values.stayType && !selectedStayType && (
                    <MenuItem value={formik.values.stayType}>Tipo anterior ({formik.values.stayType})</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormLabel sx={{ mb: 0.5, fontSize: '0.85rem', fontWeight: 500, display: 'block' }}>Entrada</FormLabel>
              <TextField
                id="checkInDate"
                name="checkInDate"
                size="small"
                type="datetime-local"
                fullWidth
                value={formik.values.checkInDate}
                onChange={formik.handleChange}
                error={formik.touched.checkInDate && Boolean(formik.errors.checkInDate)}
                helperText={formik.touched.checkInDate && formik.errors.checkInDate}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormLabel sx={{ mb: 0.5, fontSize: '0.85rem', fontWeight: 500, display: 'block' }}>Salida</FormLabel>
              <TextField
                id="checkOutDate"
                name="checkOutDate"
                size="small"
                type="datetime-local"
                fullWidth
                disabled={isHourly}
                value={formik.values.checkOutDate}
                onChange={formik.handleChange}
                error={formik.touched.checkOutDate && Boolean(formik.errors.checkOutDate)}
                helperText={
                  isHourly
                    ? `Definida por el tipo de estadía (${selectedStayType?.hours}h)`
                    : formik.touched.checkOutDate && formik.errors.checkOutDate
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3 }}>
            <SectionTitle>Tarifa</SectionTitle>

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
              {rateDelta !== 0 && (
                <Stack
                  direction="row"
                  spacing={1.25}
                  alignItems="center"
                  justifyContent="center"
                  sx={{ mb: 1.5, pb: 1.5, borderBottom: '1px dashed', borderColor: 'divider' }}
                >
                  <Chip label={`${currency} ${money(originalRate)}`} size="small" variant="outlined" />
                  <ArrowForwardIcon fontSize="small" color="action" />
                  <Chip
                    label={`${currency} ${money(newRate)}`}
                    size="small"
                    color={rateDelta > 0 ? 'warning' : 'success'}
                    sx={{ fontWeight: 700 }}
                  />
                </Stack>
              )}

              <SummaryRow label="Tarifa de la habitación" value={`${currency} ${money(newRate)}`} />
              <SummaryRow label="Adelanto pagado" value={`− ${currency} ${money(advance)}`} />
              <Divider sx={{ my: 0.5 }} />
              <SummaryRow
                label="Saldo pendiente"
                value={`${currency} ${money(newBalance)}`}
                bold
                color={newBalance > 0 ? 'error.main' : 'success.main'}
              />
            </Paper>

            {rateDelta > 0 && (
              <Alert severity="info" sx={{ mt: 1.5 }}>
                La tarifa sube{' '}
                <strong>
                  {currency} {money(rateDelta)}
                </strong>
                . Ese monto se sumará al saldo pendiente y podrás cobrarlo desde &quot;Abonar / Pagar Saldo&quot;.
              </Alert>
            )}

            {newBalance < 0 && (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                El huésped quedaría con un saldo a favor de {currency} {money(Math.abs(newBalance))}. Verifica antes de guardar.
              </Alert>
            )}

            {!room && roomData.length > 0 && (
              <Alert severity="error" sx={{ mt: 1.5 }}>
                No se encontró la habitación {data?.roomNo} para calcular la tarifa. Los montos no se actualizarán.
              </Alert>
            )}
          </Box>
        </form>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} variant="outlined" size="medium">
          Cancelar
        </Button>
        <Button onClick={formik.handleSubmit} variant="contained" color="primary" startIcon={<SaveIcon />} size="medium" disabled={!room}>
          Guardar Cambios
        </Button>
      </DialogActions>
    </Dialog>
  );
};

EditReservation.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  data: PropTypes.shape({
    _id: PropTypes.string,
    roomNo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    stayType: PropTypes.string,
    checkInDate: PropTypes.string,
    checkOutDate: PropTypes.string,
    advanceAmount: PropTypes.number,
    totalPayment: PropTypes.number
  }).isRequired
};

export default EditReservation;
